import { BasicShapeType, IHeadingState, IImageState, INoteState, IPosition, ISize, IWhiteboardShapeState } from "../rendering/shapes";
import { IOriginalImageProps } from "../rendering/shapes/image";
import { v4 as uuidv4 } from "uuid";
import { ChangeHistoryTracker } from "../rendering/state/change-history-tracker";
import { IWhiteboardLinkState } from "../rendering/links";
import { IWhiteboardStateChange } from "../rendering/state/state-change-notifier";
import { IBasicShapeState } from "@gtmhub/whiteboards/rendering/shapes/basic-shape";

export type CreateNewImageArgs = {
  position: IPosition;
  createdById: string;
  imageShapeId: string;
  imageUrl: string;
  renderingSize: ISize;
  rawImage: IOriginalImageProps;
  zIndex: number;
  rotationAngle: number;
};

export function createNewHeading(position: IPosition, createdById: string, fontFamily: string, zIndex: number): IHeadingState {
  return {
    type: "heading",
    id: uuidv4(),
    content: "",
    position,
    highlightColor: "#FFE380",
    textColor: "#000",
    fontSize: 48,
    fontFamily,
    createdById,
    zIndex,
  };
}
export function createNewNote(position: IPosition, createdById: string, zIndex: number): INoteState {
  return {
    type: "note",
    id: uuidv4(),

    content: "",
    position: position,
    size: { width: 150, height: 100 },
    backgroundColor: "#FFFAE7",
    fontSize: 12,
    fontFamily: "Arial",
    textAlignment: "left",
    createdById: createdById,
    zIndex,
  };
}

export function createBasicShape(type: BasicShapeType, position: IPosition, createdById: string, zIndex: number): IBasicShapeState {
  const defaultSize = getBasicShapeDefaultSize(type);
  return {
    type: type,
    id: uuidv4(),
    content: "",
    position: getBasicShapeDefaultPosition(type, position, defaultSize),
    size: defaultSize,
    backgroundColor: "#FFE380",
    fontSize: 12,
    fontFamily: "Arial",
    textAlignment: {
      textHorizontalAlignment: "center",
      textVerticalAlignment: "middle",
    },
    createdById: createdById,
    zIndex,
  };
}

function getBasicShapeDefaultPosition(type: BasicShapeType, position: IPosition, size: ISize): IPosition {
  if (type === "ellipse") {
    return { x: position.x + size.width / 2, y: position.y + size.height / 2 };
  }
  return position;
}

function getBasicShapeDefaultSize(type: BasicShapeType): ISize {
  switch (type) {
    case "rectangle":
    case "rounded-rectangle":
      return { width: 232, height: 111 };
    case "ellipse":
      return { width: 166, height: 166 };
    case "rhombus":
      return { width: 214, height: 182 };
    case "parallelogram":
      return { width: 202, height: 151 };
    default:
      console.warn("Unsupported basic shape type: " + type);
  }
}

export function createNewImage(newImageArgs: CreateNewImageArgs): IImageState {
  const { imageShapeId, imageUrl, renderingSize, rawImage, position, createdById, zIndex, rotationAngle } = newImageArgs;
  return {
    type: "image",
    id: imageShapeId,
    url: imageUrl,
    size: renderingSize,
    rawImage,
    position,
    createdById,
    zIndex,
    rotationAngle,
  };
}

export function onStateChangeAddShape(stateChange: IWhiteboardStateChange, changeHistoryTracker: ChangeHistoryTracker): void {
  if (stateChange.path.includes("shapes")) {
    const shape = stateChange.value as IWhiteboardShapeState;
    if (stateChange.bsonId) {
      shape.bsonId = stateChange.bsonId;
    }
    changeHistoryTracker.updateShape(shape);
  }
  if (stateChange.path.includes("links")) changeHistoryTracker.updateLink(stateChange.value as IWhiteboardLinkState);
}

export function onStateChangeRemoveShape(stateChange: IWhiteboardStateChange, changeHistoryTracker: ChangeHistoryTracker): void {
  if (stateChange.path.includes("shapes")) changeHistoryTracker.deleteShape(stateChange.value as IWhiteboardShapeState);
  if (stateChange.path.includes("links")) changeHistoryTracker.deleteLink(stateChange.value as IWhiteboardLinkState);
}

export function publishUnsyncedStateChanges(changeTracker: ChangeHistoryTracker, changes: IWhiteboardStateChange[], opts: { batch: number } = { batch: 5 }): void {
  let stateTransaction = changeTracker.beginTransaction("Publish Changes");

  const isLastIndex = (index: number, loopLength: number) => index === loopLength - 1;
  const shouldCommitTransaction = (index: number, batch: number, changesLength: number) => (index + 1) % batch === 0 || isLastIndex(index, changesLength);

  for (let i = 0; i < changes.length; i++) {
    const stateChange = changes[i];

    switch (stateChange.op) {
      case "add":
        onStateChangeAddShape(stateChange, stateTransaction);
        break;
      case "remove":
        onStateChangeRemoveShape(stateChange, stateTransaction);
        break;
    }

    if (shouldCommitTransaction(i, opts.batch, changes.length)) {
      stateTransaction.commit();

      if (!isLastIndex(i, changes.length)) {
        stateTransaction = changeTracker.beginTransaction("Publish Changes");
      }
    }
  }
}
